
import { Component, Mixins } from "vue-property-decorator";
import { ObjectUtility, UuidFactory } from "../../../../../base/utilities";
import { IQuestionnaire } from "../../../../../documaker/domain/format/objects";
import { IConfiguration } from "../../../../domain/configuration";
import { FormatPageStructureEditor, IFormatObjectEditable } from "../../../../domain/editableFormat";
import { instanceOfIQuestionnairePropertyEditable } from "../../../../domain/editableFormat/properties";
import { QuestionnaireGuidTreeEditor, QuestionnaireGuidTree } from "../../../../domain/questionnaire";

@Component
export default class QuestionnaireGuidAssignableMixin extends Mixins() {
  $configuration!: IConfiguration;

  toNewGuidDictionaryForQuestionnaire(sources: IFormatObjectEditable[]): { [key in string]: string } {
    const results: { [key in string]: string } = {};
    if (this.$configuration.formatEditor.extensions.questionnaire.isEnable) {
      sources.map(s => instanceOfIQuestionnairePropertyEditable(s) ? s.questionnaire().getValue()._guid : null)
        .filter(g => !!g)
        .forEach(g => results[g] = UuidFactory.create());
    }
    return results;
  }

  toNextSiblingIndexForQuestionnaire(source: FormatPageStructureEditor, index: number): number | undefined {
    if (this.$configuration.formatEditor.extensions.questionnaire.isEnable) {
      const editor = new QuestionnaireGuidTreeEditor(source);
      const item = editor.items[index];
      if (item) {
        return index + item.toChildren().length + 1;
      }
    }
    return typeof index === "number"
       ? index + 1
       : undefined;
  }

  assignNewGuidForQuestionnaire(source: IFormatObjectEditable, guidDictionary?: { [key in string]: string }): string {
    if (this.$configuration.formatEditor.extensions.questionnaire.isEnable && instanceOfIQuestionnairePropertyEditable(source) && (0 < source.selectableQuestionnaireTypes().length)) {
      // Questionnaire拡張時は_guidを採番する.
      const updated: IQuestionnaire = { ...source.questionnaire().getValue() };
      updated._guid = (guidDictionary && guidDictionary[updated._guid])
        ? guidDictionary[updated._guid]
        : UuidFactory.create();
      updated._childGuids = guidDictionary
        ? updated._childGuids.filter(g => guidDictionary[g]).map(g => guidDictionary[g])
        : [];
      source.questionnaire().setValue(updated);
      return updated._guid;
    }
    return null;
  }

  toRootGuidsForQuestionnaire(sources: IFormatObjectEditable[]): string[] {
    return this.$configuration.formatEditor.extensions.questionnaire.isEnable
      ? QuestionnaireGuidTree.from(sources).filter(v => !v.parent).map(v => v.guid)
      : [];
  }

  addQuestionnaireChildGuids(source: FormatPageStructureEditor, index: number, isHitObject: boolean, isNextObject: boolean, childGuids: string[]): number {
    if (this.$configuration.formatEditor.extensions.questionnaire.isEnable && typeof index === "number" && childGuids) {
      return isHitObject
        ? this.addHitObjectToQuestionnaireChildGuids(source, index, childGuids)
        : isNextObject
          ? this.addQuestionnaireChildGuidsToNextSibling(source, index, childGuids)
          : this.addQuestionnaireChildGuidsToPreviousSibling(source, index, childGuids);
    }
    return index;
  }

  private addHitObjectToQuestionnaireChildGuids(source: FormatPageStructureEditor, index: number, childGuids: string[]): number {
    const editor = new QuestionnaireGuidTreeEditor(source);
    const object = editor.objects[index];
    const item = editor.items[index];
    this.updateQuestionnaireChildGuids(source, object, item.childGuids.concat(childGuids));
    return index + item.toChildren().length + childGuids.length;
  }

  private addQuestionnaireChildGuidsToNextSibling(source: FormatPageStructureEditor, index: number, childGuids: string[]): number {
    const editor = new QuestionnaireGuidTreeEditor(source);
    const item = editor.items[index];
    if (!item) {
      return index;
    }
    if (item.parent) {
      const parentIndex = item.parent.children.findIndex(c => c.guid === item.guid);
      const prev = item.parent.children.slice(0, parentIndex + 1).map(c => c.guid);
      const next = item.parent.children.slice(parentIndex + 1, item.parent.children.length).map(c => c.guid);
      const merged = prev.concat(childGuids, next).filter(c => !!c);
      this.updateQuestionnaireChildGuids(source, item.parent.source, merged);
    }
    return index + item.toChildren().length + childGuids.length;
  }

  private addQuestionnaireChildGuidsToPreviousSibling(source: FormatPageStructureEditor, index: number, childGuids: string[]): number {
    const editor = new QuestionnaireGuidTreeEditor(source);
    const item = editor.items[index];
    if (item && item.parent) {
      const parentIndex = item.parent.children.findIndex(c => c.guid === item.guid);
      const prev = item.parent.children.slice(0, parentIndex).map(c => c.guid);
      const next = item.parent.children.slice(parentIndex, item.parent.children.length).map(c => c.guid);
      const merged = prev.concat(childGuids, next).filter(c => !!c);
      this.updateQuestionnaireChildGuids(source, item.parent.source, merged);
    }
    return index;
  }

  private updateQuestionnaireChildGuids(source: FormatPageStructureEditor, object: IFormatObjectEditable, childGuids: string[]) {
    if (!instanceOfIQuestionnairePropertyEditable(object)) {
      return;
    }
    const clone = ObjectUtility.copyDeeply(object.questionnaire().getValue()) as IQuestionnaire;
    clone._childGuids = childGuids;
    object.questionnaire().setValue(clone);
    source.updateObject(object);
  }
}
